import { useEffect, useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { L } from "@/components/public/ComplaintForm";
import { toast } from "sonner";
import { FileSearch, Send, CheckCircle2, CircleDot, Circle } from "lucide-react";
import { RecordsAttachUpload, RecordsAttachmentRow } from "@/components/public/RecordsAttachments";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";

export default function RecordsRequest() {
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState([]);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [trackNo, setTrackNo] = useState("");
  const [trackCode, setTrackCode] = useState("");
  const [track, setTrack] = useState(null);
  useEffect(() => { pub.get("/records-requests/meta").then((r) => setTypes(r.data.record_types)).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await pub.post("/records-requests", { ...f, record_types: sel });
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed."); }
    finally { setBusy(false); }
  };
  const lookup = async () => {
    try {
      const r = await pub.get(`/records-requests/${encodeURIComponent(trackNo.trim())}`, { params: { code: trackCode.trim() } });
      setTrack(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed."); }
  };
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <img src="/brand/records-seal.png" alt="Matcom Records Division seal" data-testid="records-request-seal" className="w-20 h-20 rounded-full shrink-0" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Records Division</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Public Records / FOIA Request</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>Request eligible public records (SOP-PUB-16). Records containing CJIS, victim, juvenile or officer-safety information may be redacted or withheld under applicable law.</p>
        </div>
      </div>
      {done ? (
        <div data-testid="records-receipt" className="border-2 rounded-sm p-6 space-y-3" style={{ borderColor: P.navy, background: "#fff" }}>
          <div className="font-bold text-lg" style={{ color: P.green }}>Your records request has been received.</div>
          <div className="text-sm" style={{ color: P.ink }}>Request Number: <b data-testid="records-request-number" className="font-mono text-lg">{done.request_number}</b></div>
          <div className="text-sm" style={{ color: P.ink }}>Secure Access Code: <b data-testid="records-access-code" className="font-mono text-lg" style={{ color: P.red }}>{done.access_code}</b></div>
          <div className="rounded-sm px-4 py-3 text-sm font-semibold" style={{ background: "#FBF3E2", color: "#7A5A0E", border: `1px solid ${P.gold}` }}>
            Save both — they are required to track your request below.
          </div>
          <div data-testid="records-expiry-notice" className="text-xs" style={{ color: P.dim }}>
            Retention: this request and any files you attach expire and are permanently purged 14 days after filing{done.expires_at ? ` (${new Date(done.expires_at).toLocaleDateString()})` : ""}.
          </div>
          <div className="pt-2 border-t" style={{ borderColor: P.line }}>
            <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: P.navy }}>Attach supporting files now (optional)</div>
            <RecordsAttachUpload no={done.request_number} code={done.access_code} onDone={() => toast.success("Files attached to your request")} />
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><L>Full Name *</L><input data-testid="records-name" className={field} style={{ borderColor: P.line }} value={f.name || ""} onChange={set("name")} /></div>
            <div><L>Email</L><input data-testid="records-email" className={field} style={{ borderColor: P.line }} value={f.email || ""} onChange={set("email")} /></div>
            <div><L>Telephone</L><input className={field} style={{ borderColor: P.line }} value={f.phone || ""} onChange={set("phone")} /></div>
          </div>
          <div>
            <L>Record Types * ({sel.length} selected)</L>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {types.map((t) => (
                <button key={t} type="button" data-testid={`records-type-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => setSel((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]))}
                  className="text-left px-3 py-2 text-sm rounded-sm border"
                  style={sel.includes(t) ? { background: P.navy, color: "#fff", borderColor: P.navy } : { background: "#fff", color: P.ink, borderColor: P.line }}>
                  {sel.includes(t) ? "✓ " : ""}{t}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L>Date Range of Records</L><input className={field} style={{ borderColor: P.line }} value={f.date_range || ""} onChange={set("date_range")} placeholder="e.g. Jan 1 – Feb 10, 2026" /></div>
            <div><L>Preferred Format</L>
              <select className={field} style={{ borderColor: P.line }} value={f.preferred_format || "Electronic"} onChange={set("preferred_format")}>
                <option>Electronic</option><option>Paper</option><option>Inspection Only</option>
              </select></div>
          </div>
          <div><L>Describe the Records Requested *</L>
            <textarea data-testid="records-description" className={field} style={{ borderColor: P.line }} rows={4} value={f.description || ""} onChange={set("description")} placeholder="Be as specific as possible — incident numbers, dates, locations…" /></div>
          <button data-testid="records-submit" disabled={busy} onClick={submit}
            className="w-full py-3 text-sm font-bold text-white rounded-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50" style={{ background: P.navy }}>
            <FileSearch size={15} /> {busy ? "SUBMITTING…" : "SUBMIT RECORDS REQUEST"}
          </button>
        </div>
      )}
      <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Track an Existing Request</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="records-track-no" className={field} style={{ borderColor: P.line }} value={trackNo} onChange={(e) => setTrackNo(e.target.value)} placeholder="PR-2026-0001" />
          <input data-testid="records-track-code" className={`${field} font-mono`} style={{ borderColor: P.line }} value={trackCode} onChange={(e) => setTrackCode(e.target.value)} placeholder="Access code" />
          <button data-testid="records-track-btn" onClick={lookup} className="px-5 py-2.5 text-sm font-bold text-white rounded-sm shrink-0" style={{ background: P.gold }}>TRACK</button>
        </div>
        {track && (
          <div data-testid="records-track-result" className="pt-2 space-y-3">
            <div className="font-mono font-bold" style={{ color: P.navy }}>{track.request_number} — {track.status.toUpperCase()}{track.disposition ? ` · ${track.disposition}` : ""}</div>
            {track.expires_at && (
              <div data-testid="records-track-expiry" className="text-xs px-3 py-2 rounded-sm" style={{ background: track.purged ? "#FDECEC" : "#FBF3E2", color: track.purged ? P.red : "#7A5A0E", border: `1px solid ${track.purged ? P.red : P.gold}` }}>
                {track.purged ? "This request's contents were purged under the 14-day retention policy." : `Contents expire ${new Date(track.expires_at).toLocaleString()} (14-day retention).`}
              </div>
            )}
            {(track.attachments || []).length > 0 && (
              <div data-testid="records-track-attachments" className="space-y-1">
                <div className="text-[10px] font-bold uppercase" style={{ color: P.dim }}>Attached files ({track.attachments.length})</div>
                {track.attachments.map((a) => (
                  <RecordsAttachmentRow key={a.aid} a={a} href={`${pub.defaults.baseURL}/records-requests/${encodeURIComponent(trackNo.trim())}/attachments/${a.aid}?code=${encodeURIComponent(trackCode.trim())}`} />
                ))}
              </div>
            )}
            {!track.purged && <RecordsAttachUpload no={trackNo.trim()} code={trackCode.trim()} onDone={lookup} compact />}
            <div className="flex flex-wrap gap-4">
              {track.timeline.map((t) => (
                <div key={t.stage} data-testid={`records-stage-${t.stage}`} className="flex items-center gap-1.5 text-xs" style={{ color: t.state === "pending" ? P.dim : P.ink }}>
                  {t.state === "done" ? <CheckCircle2 size={15} style={{ color: P.green }} /> : t.state === "active" ? <CircleDot size={15} style={{ color: P.gold }} /> : <Circle size={15} style={{ color: P.line }} />}
                  {t.label}
                </div>
              ))}
            </div>
            {(track.messages || []).map((m, i) => (
              <div key={i} data-testid={m.kind === "expiry_reminder" ? "records-expiry-reminder" : undefined} className="p-2.5 rounded-sm text-sm" style={m.kind === "expiry_reminder" ? { background: "#FDECEC", border: `1px solid ${P.red}` } : { background: m.from === "citizen" ? "#F1EFE8" : "#EEF2F6", border: `1px solid ${P.line}` }}>
                <div className="text-[10px] font-bold uppercase" style={{ color: m.kind === "expiry_reminder" ? P.red : P.dim }}>{m.author} · {new Date(m.ts).toLocaleString()}</div>
                <div style={{ color: P.ink }}>{m.text}</div>
              </div>
            ))}
            <div className="flex gap-2">
              <input data-testid="records-reply-input" className={field} style={{ borderColor: P.line }} value={f._reply || ""} onChange={set("_reply")} placeholder="Add a message to your request…" />
              <button data-testid="records-reply-send" onClick={async () => {
                if (!(f._reply || "").trim()) return;
                await pub.post(`/records-requests/${encodeURIComponent(trackNo.trim())}/messages`, { code: trackCode.trim(), text: f._reply });
                setF((p) => ({ ...p, _reply: "" })); lookup();
              }} className="px-4 rounded-sm text-white shrink-0" style={{ background: P.navy }}><Send size={15} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
